/**
 * Current Interview Actions Component
 * Action buttons for current interview scorecard rows
 */

import {
  Button,
  ButtonGroup,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  IconButton,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { KText, KTag } from '@/ui-library/components';
import { Eye } from '@kula-ai/icons/outline/Eye';
import { Edit05 } from '@kula-ai/icons/outline/Edit05';
import { Bell01 } from '@kula-ai/icons/outline/Bell01';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import {
  type ScorecardItem,
  type ScorecardVisibilityMode,
} from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/types';
import {
  isOwnScorecard,
  canViewOtherScorecard,
} from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/helpers';
import { useSendReminderMutation } from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/hooks';
import { OverrideScorecardModal } from '@/components/ats/collect-score-card/OverrideScorecardModal';
import { type InterviewType } from '@/types/interview';

interface CurrentInterviewActionsProps {
  interview: InterviewType;
  scorecard: ScorecardItem;
  currentUserId: number;
  mode: ScorecardVisibilityMode;
  hasCurrentUserSubmitted: boolean;
  onSubmitScorecard: (_: ScorecardItem) => void;
  onViewScorecard: (_: ScorecardItem) => void;
  onEditScorecard: (_: ScorecardItem) => void;
  onSendReminder: (_: ScorecardItem) => void;
  refetch?: () => void;
}

export const CurrentInterviewActions = ({
  interview,
  scorecard,
  currentUserId,
  mode,
  hasCurrentUserSubmitted,
  onSubmitScorecard,
  onViewScorecard,
  onEditScorecard,
  refetch,
}: CurrentInterviewActionsProps) => {
  const draftScActions = useDisclosure();
  const currUser = useSelector(selectCurrUser);

  const { mutate: sendReminder, isPending: isReminderLoading } =
    useSendReminderMutation();

  const isOwn = isOwnScorecard(scorecard, currentUserId);
  const isSubmitted = scorecard.status === 'submitted';
  const canView = canViewOtherScorecard(
    scorecard,
    currentUserId,
    mode,
    hasCurrentUserSubmitted,
    interview
  );

  // Check if another user has a draft for this scorecard
  const isSomeOtherUserHasDraftScorecard =
    scorecard.status === 'draft' &&
    scorecard.submitted_by?.id &&
    scorecard.submitted_by?.id !== currUser?.id;

  const handleSendReminder = () => {
    sendReminder(
      { scorecardId: scorecard.id },
      {
        onSuccess: () => {
          refetch?.();
        },
      }
    );
  };

  const handleSubmitClick = () => {
    if (isSomeOtherUserHasDraftScorecard) {
      draftScActions.onOpen();
    } else {
      onSubmitScorecard(scorecard);
    }
  };

  // Case 1: Own scorecard - not submitted
  if (isOwn && !isSubmitted) {
    return (
      <Flex justifyContent="flex-end" flex="1">
        <Button
          variant="quaternary"
          leftIcon={<Icon as={Edit05} />}
          onClick={handleSubmitClick}
          size="sm"
        >
          Submit scorecard
        </Button>
        {draftScActions.isOpen && (
          <OverrideScorecardModal
            draftScActions={draftScActions}
            scorecard={scorecard}
            onCTAClick={() => {
              onSubmitScorecard(scorecard);
              draftScActions.onClose();
            }}
          />
        )}
      </Flex>
    );
  }

  // Case 2: Own scorecard - submitted
  if (isOwn && isSubmitted) {
    return (
      <Flex gap="8px" justifyContent="flex-end" flex="1">
        {scorecard.can_edit && (
          <Button
            variant="quaternary"
            leftIcon={<Icon as={Edit05} w="14px" h="14px" />}
            onClick={() => onEditScorecard(scorecard)}
            size="sm"
          >
            Edit
          </Button>
        )}
        <Button
          variant="quaternary"
          leftIcon={<Icon as={Eye} w="14px" h="14px" />}
          onClick={() => onViewScorecard(scorecard)}
          size="sm"
        >
          View
        </Button>
      </Flex>
    );
  }

  // Case 3: Other's scorecard - submitted (and can view)
  if (!isOwn && isSubmitted && canView) {
    return (
      <Flex justifyContent="flex-end" flex="1">
        <Button
          variant="quaternary"
          leftIcon={<Icon as={Eye} w="14px" h="14px" />}
          onClick={() => onViewScorecard(scorecard)}
          size="sm"
        >
          View
        </Button>
      </Flex>
    );
  }

  // Case 4: Other's scorecard - not submitted (send reminder + submit yourself dropdown)
  if (!isOwn && !isSubmitted) {
    return (
      <Flex justifyContent="flex-end" flex="1" alignItems="center">
        <ButtonGroup isAttached variant="quaternary">
          <Button
            leftIcon={<Icon as={Bell01} />}
            onClick={handleSendReminder}
            isLoading={isReminderLoading}
            size="sm"
            _hover={{}}
            _focus={{}}
            _active={{}}
          >
            Send Reminder
            <KTag
              borderRadius="50%"
              w="20px"
              h="20px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="12px"
              fontWeight="650"
              marginLeft="4px"
              value={scorecard?.reminder_count}
            />
          </Button>
          {scorecard.can_edit && (
            <Menu placement="bottom-end" strategy="fixed">
              <MenuButton
                as={IconButton}
                variant="quaternary"
                icon={<Icon as={ChevronDown} />}
                borderLeftRadius="0px"
                borderLeft="none"
                px="0"
                minW="unset"
                w="2rem"
                size="sm"
                aria-label="More actions"
              />
              <Portal>
                <MenuList zIndex={9999}>
                  <MenuItem
                    gap="8px"
                    onClick={handleSubmitClick}
                    _hover={{ fontWeight: 'semibold' }}
                  >
                    <Icon as={Edit05} width="16px" height="16px" />
                    <KText
                      size="sm-medium"
                      fontWeight="inherit"
                      color="secondary-900"
                    >
                      Submit scorecard yourself
                    </KText>
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          )}
        </ButtonGroup>
        {draftScActions.isOpen && (
          <OverrideScorecardModal
            draftScActions={draftScActions}
            scorecard={scorecard}
            onCTAClick={() => {
              onSubmitScorecard(scorecard);
              draftScActions.onClose();
            }}
          />
        )}
      </Flex>
    );
  }

  // Default: No actions available
  return null;
};
